import { PageTypeEnum } from '@/constant';
import {
	useDeleteListSubmit,
	useGetListData,
	useMergingState,
	useSearchFilterParams,
} from '@/hooks';
import { TGetListUserSettingResponse } from '@/services';
import { cn, insertObjectIfElse } from '@/utils';
import { memo, useCallback, useEffect, useMemo } from 'react';

import { DefaultOptionType } from 'antd/es/select';
import { TablePaginationConfig } from 'antd/es/table';
import { SorterResult } from 'antd/es/table/interface';

import { isFalse, isNotNilOrEmpty, isNotUndefined } from 'ramda-adjunct';

import Table from '..';
import Actions from '../components/actions';
import { getSortSql } from '../config';
import { ColumnTypes, TableProps, TableWithActionProps } from '../type';

const TableWithActions = ({
	searchFilterRef,
	pagination,
	onPageSizeChange,
	onPageChange,
	onDelete,
	columns,
	containerClassName,
	...rest
}: TableWithActionProps) => {
	const [state, setState] = useMergingState({
		columns: [] as typeof columns,
		settingListFields: [] as TGetListUserSettingResponse,
	});

	useEffect(() => {
		const newColumns = getNewColumns(state.settingListFields, columns);
		setState({ columns: newColumns });
	}, [columns, state.settingListFields]);

	useEffect(() => {
		rest?.rowSelection?.onReset?.();
	}, [searchFilterRef?.current?.isSearch]);

	const { onDeleteSubmit } = useDeleteListSubmit({
		tableType: rest.tableType,
		searchFilterRef,
		rowSelection: rest.rowSelection,
		onDeleteSuccessCallback: rest.onDeleteSuccessCallback,
		onDeleteFailedCallback: rest.onDeleteFailedCallback,
	});

	const {
		list,
		fetching: loading,
		total,
		params,
	} = useGetListData({
		type: rest?.tableType,
	});

	const reduxPagination: TablePaginationConfig = useMemo(() => {
		return {
			defaultPageSize: params?.pageSize,
			pageSize: params?.pageSize,
			current: params?.page,
			total: total,
		};
	}, [params, total]);

	const [_, setSearchParams] = useSearchFilterParams(rest.tableType || '');

	const getNewColumns = useCallback(
		(
			settingListFields: TGetListUserSettingResponse,
			inputColumns?: ColumnTypes
		) => {
			if (!settingListFields?.length) return inputColumns;

			const _settingListFields = settingListFields?.reduce(
				(acc, item) => {
					acc[item.objectName] = [...(item?.fields || [])];
					return acc;
				},
				{} as { [key in PageTypeEnum | number]: string[] }
			);

			const mapColumns = (item: ColumnTypes[number]) => {
				if (!item?.children && item?.title !== 'ID') {
					const checkedValue =
						_settingListFields[item?.objectName as PageTypeEnum] || [];
					return {
						...item,
						hidden: isNotUndefined(item.hidden)
							? item.hidden
							: !checkedValue?.includes(item.key as string),
					};
				}

				const newItem = {
					...item,
				};

				if (item?.children) {
					const checkedValue =
						_settingListFields[item?.objectName as PageTypeEnum] || [];

					const newItemChildren = item.children
						?.map(mapColumns)
						.sort(
							(a, b) =>
								checkedValue?.indexOf(a.key as string) -
								checkedValue?.indexOf(b.key as string)
						);

					newItem.children = newItemChildren;

					if (newItemChildren.every((item) => item.hidden)) {
						newItem.hidden = true;
					} else {
						newItem.hidden = false;
					}
				}

				return newItem;
			};

			const newColumns = inputColumns?.map(mapColumns)?.sort((a, b) => {
				return (
					_settingListFields[a?.objectName as PageTypeEnum]?.indexOf(
						a.key as string
					) -
					_settingListFields[b?.objectName as PageTypeEnum]?.indexOf(
						b.key as string
					)
				);
			});

			return newColumns;
		},
		[]
	);

	const _onPageSizeChange = useCallback(
		(
			value: DefaultOptionType['value'],
			option: DefaultOptionType | DefaultOptionType[]
		) => {
			onPageSizeChange?.(value, option);

			searchFilterRef?.current?.onPageSizeChange?.(value, option);
		},
		[onPageSizeChange, searchFilterRef?.current]
	);

	const _onPageChange = useCallback(
		(page: number, pageSize: number) => {
			setSearchParams({
				page,
				pageSize,
			});

			onPageChange?.(page, pageSize);

			searchFilterRef?.current?.onPageChange?.(page, pageSize);
		},
		[setSearchParams, onPageChange, searchFilterRef?.current]
	);

	const handleSave = useCallback(
		(
			settingListFields: TGetListUserSettingResponse,
			tabActive?: string | undefined
		) => {
			setState({
				settingListFields,
			});
		},
		[]
	);

	const _onDelete = useCallback(() => {
		onDeleteSubmit(() => {
			rest.rowSelection?.onReset?.();
		});

		onDelete?.();
	}, [onDelete, onDeleteSubmit, rest.rowSelection?.onReset]);

	const shouldHidePagination = useMemo(() => {
		return (
			isFalse(pagination) ||
			(isNotUndefined(pagination) &&
				//@ts-expect-error: ignore error
				(pagination?.pageSize || 0) >=
					//@ts-expect-error: ignore error
					(pagination?.total || 0)) ||
			(!isNotUndefined(pagination) &&
				(reduxPagination?.pageSize || 0) >= (reduxPagination?.total || 0))
		);
	}, [pagination, reduxPagination]);

	const onTableChange: TableProps['onChange'] = (...args) => {
		rest.onChange?.(...args);

		if (args[3]?.action === 'sort') {
			const sorter = args[2] as SorterResult<any>;

			const sortSql = getSortSql({ sorter });
			searchFilterRef?.current?.setSorter?.({ sortSql });
		}
	};

	return (
		<div
			className={cn(
				'h-full w-full card !space-y-2.5',
				isNotNilOrEmpty(rest?.titleInDetail) && 'pt-[15px]',
				containerClassName
			)}
		>
			<Actions
				defaultPageSize={params?.pageSize}
				onPageSizeChange={_onPageSizeChange}
				onSave={handleSave}
				onDelete={_onDelete}
				columns={state.columns}
				dataSource={list}
				searchFilterRef={searchFilterRef}
				{...rest}
			/>

			<Table
				loading={loading}
				pagination={
					shouldHidePagination
						? false
						: {
								...insertObjectIfElse(
									isNotUndefined(pagination),
									pagination,
									reduxPagination
								),
								onChange: (page: number, pageSize: number) =>
									isNotUndefined(pagination) &&
									pagination &&
									'pageSizeLocal' in pagination
										? _onPageChange(page, pagination?.pageSizeLocal as number)
										: _onPageChange(page, pageSize),
							}
				}
				columns={state.columns}
				dataSource={list}
				searchFilterRef={searchFilterRef}
				settingListFields={state.settingListFields}
				{...rest}
				onChange={onTableChange}
			/>
		</div>
	);
};

export default memo(TableWithActions);
